import { Injectable } from '@angular/core';
import { Contact } from '@app/contacts/models/contact';
import { DbWorkerService } from '@app/core/db/db-worker.service';
import { skySwitchDB } from '@app/core/db/skyswitch-db';
import { Document as FlexDocument, DocumentData } from 'flexsearch';
import { BehaviorSubject, filter, map, Observable, Subject } from 'rxjs';

import { Voicemail } from '../models/voicemail.models';

/** Max items persisted to IDB. In-memory cache grows beyond this during the session. */
export const VOICEMAIL_CACHE_BOUND = 500;

/**
 * Shape of data indexed in FlexSearch for voicemail items.
 */
interface IndexedVoicemail extends DocumentData {
  id: string;
  label: string;
  tel: string;
  contactName: string;
  contactExt: string;
}

@Injectable({
  providedIn: 'root',
})
export class VoicemailStorageService {
  private static readonly CACHE_BOUND = VOICEMAIL_CACHE_BOUND;

  /** Fields that affect voicemail search index when a contact is updated */
  private static readonly NAME_RELATED_FIELDS: Array<keyof Contact> = [
    'fullName',
    'firstName',
    'lastName',
    'company',
    'ext',
  ];

  // ========== FlexSearch Index ==========

  private readonly searchIndex: FlexDocument<IndexedVoicemail>;

  /** Callback to get contact by ID (set by VoicemailService after contacts load) */
  private getContactById?: (contactId: string) => Contact | undefined;

  /** Subject to notify when search index is ready */
  private readonly searchIndexReadySubject = new Subject<void>();
  public readonly searchIndexReady$ = this.searchIndexReadySubject.asObservable();

  // ========== In-Memory Store ==========

  private readonly itemsMap = new Map<string, Voicemail>(); // O(1) lookup by id
  private sortedItems: Voicemail[] = []; // Sorted by dateTime DESC

  // Single notification subject — subscribers filter by ID, auto-cleaned on unsubscribe
  private readonly itemChangedSubject = new Subject<{ id: string; value: Voicemail | undefined }>();

  // Cache-level observables (replace liveQuery for counts)
  private readonly countSubject = new BehaviorSubject<number>(0);

  // Emits when in-memory cache changes structurally
  private readonly cacheChangedSubject = new Subject<void>();
  public readonly cacheChanged$ = this.cacheChangedSubject.asObservable();

  /**
   * Observable that emits when bulk data operations complete.
   * DataSources subscribe to this to know when to refresh their cache.
   */
  public readonly bulkDataChanged$ = this.cacheChangedSubject.asObservable();

  constructor(private readonly dbWorker: DbWorkerService) {
    this.searchIndex = new FlexDocument<IndexedVoicemail>({
      document: {
        id: 'id',
        index: [
          { field: 'label', tokenize: 'full', encoder: 'LatinDefault' },
          { field: 'tel', tokenize: 'full', encoder: 'Exact' },
          { field: 'contactName', tokenize: 'full', encoder: 'LatinDefault' },
          { field: 'contactExt', tokenize: 'full', encoder: 'Exact' },
        ],
      },
    });

    this.setUpContactObservers();
  }

  // ========== Contact Lookup ==========

  public setContactLookup(getContactById: (contactId: string) => Contact | undefined): void {
    this.getContactById = getContactById;
  }

  // ========== Seed ==========

  /**
   * Seed the in-memory cache from IDB data (called once at startup).
   * Items are expected to be sorted by dateTime DESC from the worker.
   *
   * Uses merge semantics: items whose id is already in the cache are skipped.
   * This protects against a WebSocket event landing in the cache before the
   * seed completes — the live item wins over the (older) seeded copy.
   */
  public seedInMemory(items: Voicemail[]): void {
    const startTime = performance.now();

    for (const item of items) {
      if (this.itemsMap.has(item.id)) continue;
      this.insertSorted(item);
    }

    this.countSubject.next(this.sortedItems.length);
    this.buildSearchIndex();

    // Notify any active observers with fresh data
    for (const [id, item] of this.itemsMap) {
      this.notifyItemChange(id, item);
    }

    this.cacheChangedSubject.next();

    const elapsed = performance.now() - startTime;
    console.log(`[Perf][voicemail] seedInMemory: ${items.length} items seeded in ${elapsed.toFixed(1)}ms`);
  }

  // ========== Search Index Management ==========

  public buildSearchIndex(): void {
    const startTime = performance.now();
    this.searchIndex.clear();

    for (const item of this.sortedItems) {
      this.addToSearchIndex(item);
    }

    const elapsed = performance.now() - startTime;
    console.log(
      `[Perf][Voicemail-Search] Search index built: ${this.sortedItems.length} items indexed in ${elapsed.toFixed(1)}ms`
    );
    this.searchIndexReadySubject.next();
  }

  public addToSearchIndex(vm: Voicemail): void {
    this.searchIndex.add(this.buildIndexPayload(vm));
  }

  public updateInSearchIndex(vm: Voicemail): void {
    this.searchIndex.update(this.buildIndexPayload(vm));
  }

  public removeFromSearchIndex(id: string): void {
    this.searchIndex.remove(id);
  }

  public search(keyword: string): string[] {
    const query = keyword.toLowerCase();
    const results = this.searchIndex.search(query, {
      index: ['label', 'tel', 'contactName', 'contactExt'],
      limit: 10000,
    });
    return Array.from(new Set(results.flatMap((r) => r.result))) as string[];
  }

  private buildIndexPayload(vm: Voicemail): IndexedVoicemail {
    const contact = vm.contactId ? this.getContactById?.(vm.contactId) : undefined;
    return {
      id: vm.id,
      label: (vm.label ?? '').toLowerCase(),
      tel: vm.tel ?? '',
      contactName: contact?.fullName?.toLowerCase() ?? '',
      contactExt: contact?.ext ?? '',
    };
  }

  // ========== In-Memory Read Methods ==========

  public getByIds(ids: string[]): Voicemail[] {
    if (ids.length === 0) return [];
    return ids.map((id) => this.itemsMap.get(id)).filter((item): item is Voicemail => !!item);
  }

  public getByIdsInRange(ids: string[], start: number, limit: number): Voicemail[] {
    return this.getByIds(ids.slice(start, start + limit));
  }

  public getItemsInRange(start: number, limit: number, predicate?: (item: Voicemail) => boolean): Voicemail[] {
    if (predicate) {
      return this.sortedItems.filter(predicate).slice(start, start + limit);
    }
    return this.sortedItems.slice(start, start + limit);
  }

  public getCount(predicate?: (item: Voicemail) => boolean): number {
    if (predicate) return this.sortedItems.filter(predicate).length;
    return this.sortedItems.length;
  }

  public getAllSortedItems(): Voicemail[] {
    return [...this.sortedItems];
  }

  public getFilteredItems(predicate: (item: Voicemail) => boolean): Voicemail[] {
    return this.sortedItems.filter(predicate);
  }

  public getLastItem(): Voicemail | undefined {
    return this.sortedItems[this.sortedItems.length - 1];
  }

  /** Synchronous read from the in-memory cache. */
  public get(id: string): Voicemail | undefined {
    return this.itemsMap.get(id);
  }

  // ========== Observables ==========

  public observeVoicemailById(id: string): Observable<Voicemail | undefined> {
    return new Observable<Voicemail | undefined>((subscriber) => {
      const sub = this.itemChangedSubject
        .pipe(
          filter((change) => change.id === id),
          map((change) => change.value)
        )
        .subscribe(subscriber);
      subscriber.next(this.itemsMap.get(id));
      return sub;
    });
  }

  public observeCount(): Observable<number> {
    return this.countSubject.asObservable();
  }

  // ========== Contact Change Handling ==========

  private setUpContactObservers(): void {
    skySwitchDB.contactUpdatedSubject.asObservable().subscribe(({ contact, mods }) => {
      const nameChanged = VoicemailStorageService.NAME_RELATED_FIELDS.some((field) => field in mods);
      if (nameChanged) {
        this.reindexForContact(contact);
      }
    });
  }

  private reindexForContact(contact: Contact): void {
    for (const vm of this.sortedItems) {
      if (vm.contactId === contact.id) {
        this.updateInSearchIndex(vm);
      }
    }
  }

  // ========== Core Write Operations ==========

  public updateVoicemailItem(id: string, fieldsToUpdate: Partial<Voicemail>): void {
    const existing = this.itemsMap.get(id);
    if (!existing) return;

    Object.assign(existing, fieldsToUpdate);
    this.updateInSearchIndex(existing);
    this.notifyItemChange(id, existing);
    this.cacheChangedSubject.next();

    this.dbWorker.callWorker('bulkPut', { table: 'voicemails', items: [existing] });
  }

  public removeVoicemailItem(id: string): void {
    const existing = this.itemsMap.get(id);
    if (!existing) return;

    const idx = this.sortedItems.indexOf(existing);
    if (idx !== -1) this.sortedItems.splice(idx, 1);
    this.itemsMap.delete(id);
    this.removeFromSearchIndex(id);
    this.countSubject.next(this.sortedItems.length);
    this.notifyItemChange(id, undefined);
    this.cacheChangedSubject.next();

    this.dbWorker.callWorker('bulkDelete', { table: 'voicemails', keys: [id] });
  }

  public upsertVoicemail(items: Voicemail[]): { lastContiguousTimestamp: string | null } {
    const upsertStart = performance.now();

    if (items.length === 0) {
      const lastItem = this.getLastItem();
      return { lastContiguousTimestamp: lastItem?.dateTime ?? null };
    }

    for (const item of items) {
      const existing = this.itemsMap.get(item.id);
      if (existing) {
        // Sort key changed — remove + re-insert to maintain dateTime DESC invariant.
        // In practice dateTime is immutable after creation; we guard defensively.
        if (existing.dateTime !== item.dateTime) {
          const idx = this.sortedItems.indexOf(existing);
          if (idx !== -1) this.sortedItems.splice(idx, 1);
          Object.assign(existing, item);
          this.insertSorted(existing);
        } else {
          Object.assign(existing, item);
        }
        this.updateInSearchIndex(existing);
        this.notifyItemChange(item.id, existing);
      } else {
        this.insertSorted(item);
        this.addToSearchIndex(item);
      }
    }

    this.countSubject.next(this.sortedItems.length);
    this.cacheChangedSubject.next();

    // Fire-and-forget IDB persistence.
    // Small batches (WebSocket events): write just those items.
    // Large batches (API page loads): full bounded write-back with eviction.
    if (items.length <= 10) {
      this.dbWorker.callWorker('bulkPut', {
        table: 'voicemails',
        items: items.map((item) => this.itemsMap.get(item.id) ?? item),
      });
    } else {
      this.writeBackToIDB();
    }

    const lastItem = this.getLastItem() ?? items.at(-1);
    const lastContiguousTimestamp = lastItem?.dateTime || null;

    const totalMs = performance.now() - upsertStart;
    console.log(`[Perf][voicemail] upsertVoicemail: ${items.length} items | total=${totalMs.toFixed(1)}ms`);

    return { lastContiguousTimestamp };
  }

  /**
   * Remove voicemails for shared mailboxes the user no longer has access to.
   *
   * Two independent passes:
   *   1) In-memory cleanup — walks `sortedItems`. May find nothing if the seed
   *      hasn't populated the cache yet; that's fine.
   *   2) IDB orphan sweep — always runs. Uses Dexie's `uniqueKeys()` on the
   *      `shared_uuid` index to discover which distinct uuids exist on disk,
   *      then deletes rows for any uuid not in `validUuids`. This is the only
   *      mechanism that cleans rows persisted from a prior session for a
   *      now-revoked mailbox that didn't land in this session's seed window.
   *
   * The IDB sweep is intentionally independent of the in-memory walk so an
   * early API response (before the seed completes) still triggers orphan
   * cleanup instead of bailing on an empty cache.
   */
  public async purgeSharedVoicemailsNotIn(validUuids: Set<string>): Promise<void> {
    // 1) In-memory cleanup
    const idsToRemove: string[] = [];
    for (const vm of this.sortedItems) {
      if (vm.shared_uuid && !validUuids.has(vm.shared_uuid)) {
        idsToRemove.push(vm.id);
      }
    }

    if (idsToRemove.length > 0) {
      const removalSet = new Set(idsToRemove);
      for (const id of idsToRemove) {
        this.itemsMap.delete(id);
        this.removeFromSearchIndex(id);
        this.notifyItemChange(id, undefined);
      }
      this.sortedItems = this.sortedItems.filter((vm) => !removalSet.has(vm.id));

      this.countSubject.next(this.sortedItems.length);
      this.cacheChangedSubject.next();

      this.dbWorker.callWorker('bulkDelete', { table: 'voicemails', keys: idsToRemove });
    }

    // 2) IDB orphan sweep — independent of cache state
    try {
      const allUuids = (await skySwitchDB.voicemails.orderBy('shared_uuid').uniqueKeys()) as string[];
      const revoked = allUuids.filter((u): u is string => !!u && !validUuids.has(u));
      if (revoked.length > 0) {
        await skySwitchDB.voicemails.where('shared_uuid').anyOf(revoked).delete();
      }
    } catch (error) {
      console.error('[voicemail] orphan purge failed:', error);
    }
  }

  public async drop(): Promise<void> {
    const oldIds = Array.from(this.itemsMap.keys());

    this.searchIndex.clear();
    this.itemsMap.clear();
    this.sortedItems = [];
    this.countSubject.next(0);

    for (const id of oldIds) {
      this.notifyItemChange(id, undefined);
    }

    this.cacheChangedSubject.next();

    await skySwitchDB.resetVoicemails();
  }

  // ========== Cache Trimming ==========

  /**
   * Trim the in-memory cache back to CACHE_BOUND items.
   * Called when search auto-continue ends and the user clears their search.
   */
  public trimToOriginalBound(): void {
    const bound = VoicemailStorageService.CACHE_BOUND;
    if (this.sortedItems.length <= bound) return;

    const excess = this.sortedItems.splice(bound);
    for (const item of excess) {
      this.itemsMap.delete(item.id);
      this.removeFromSearchIndex(item.id);
    }

    this.countSubject.next(this.sortedItems.length);
    this.cacheChangedSubject.next();
    this.writeBackToIDB();
  }

  // ========== Private Helpers ==========

  /**
   * Insert an item in sorted position (dateTime DESC) using binary search.
   * Also sets itemsMap.
   */
  private insertSorted(item: Voicemail): void {
    this.itemsMap.set(item.id, item);

    let low = 0;
    let high = this.sortedItems.length;
    const itemTime = item.dateTime;

    while (low < high) {
      const mid = (low + high) >>> 1;
      if (this.sortedItems[mid].dateTime >= itemTime) {
        low = mid + 1;
      } else {
        high = mid;
      }
    }

    this.sortedItems.splice(low, 0, item);
  }

  private notifyItemChange(id: string, item: Voicemail | undefined): void {
    this.itemChangedSubject.next({ id, value: item });
  }

  /**
   * Write the most recent CACHE_BOUND items to IDB and evict excess.
   * In-memory cache retains all items for the session.
   */
  private writeBackToIDB(): void {
    const bound = VoicemailStorageService.CACHE_BOUND;
    const itemsToWrite = this.sortedItems.slice(0, bound);
    this.dbWorker.callWorker('bulkPut', { table: 'voicemails', items: itemsToWrite });

    if (this.sortedItems.length > bound) {
      const excessIds = this.sortedItems.slice(bound).map((item) => item.id);
      this.dbWorker.callWorker('bulkDelete', { table: 'voicemails', keys: excessIds });
    }
  }
}
